import { useEffect, useMemo } from "react";
import { ErrorBoundary, Suspense } from "@suspensive/react";
import { SuspenseQuery } from "@suspensive/react-query";
import { QueryErrorResetBoundary, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AccountDeletionSurveyTable } from "@/components/account-deletion-surveys/account-deletion-survey-table";
import { AccountDeletionReasonSummary } from "@/components/account-deletion-surveys/account-deletion-reason-summary";
import { accountDeletionSurveysQueryOptions } from "@/hooks/use-account-deletion-surveys";
import { usePagination } from "@/hooks/use-pagination";
import { TableLoadingFallback } from "@/components/suspense/loading-fallback";
import { SectionErrorFallback } from "@/components/error-boundary/error-fallback";

export default function AccountDeletionSurveyPage() {
  const pagination = usePagination({ initialLimit: 20 });
  const queryClient = useQueryClient();

  useEffect(() => {
    queryClient.prefetchQuery(
      accountDeletionSurveysQueryOptions({
        page: pagination.page + 1,
        limit: pagination.limit,
      }),
    );
  }, [queryClient, pagination.page, pagination.limit]);

  const queryOptions = useMemo(
    () =>
      accountDeletionSurveysQueryOptions({
        page: pagination.page,
        limit: pagination.limit,
      }),
    [pagination.page, pagination.limit],
  );

  return (
    <div className="space-y-4 md:space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">회원 탈퇴 설문</h1>
        <p className="text-muted-foreground leading-normal">
          사유별 응답 수와, 상세 메시지를 남긴 응답을 조회합니다.
        </p>
      </div>

      <QueryErrorResetBoundary>
        {({ reset }) => (
          <ErrorBoundary onReset={reset} fallback={SectionErrorFallback}>
            <Suspense fallback={<TableLoadingFallback columnCount={5} />}>
              <SuspenseQuery {...queryOptions}>
                {({ data }) => (
                  <div className="space-y-4 md:space-y-6">
                    <div className="space-y-2">
                      <h2 className="text-sm font-medium text-muted-foreground">
                        사유별 응답 수
                      </h2>
                      <AccountDeletionReasonSummary
                        reasonCounts={data.reasonCounts}
                      />
                    </div>

                    <Card>
                      <CardHeader>
                        <CardTitle>상세 메시지 응답</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4 px-4 md:px-6">
                        <AccountDeletionSurveyTable
                          data={data.items}
                          meta={data.meta}
                          pagination={pagination}
                        />
                      </CardContent>
                    </Card>
                  </div>
                )}
              </SuspenseQuery>
            </Suspense>
          </ErrorBoundary>
        )}
      </QueryErrorResetBoundary>
    </div>
  );
}
